import { OfficeMember } from '@/components/OfficeMember';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import {
  ListView,
  ListViewLabel,
  ListViewProps,
  Single,
} from '@moneyforward/ap-frontend-components';
import { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

export type Props = {
  container?: HTMLElement | null;
  selectedItems?: string[];
  // onSelectedItems?: (officeMemberIds: string[]) => void;
  defaultOptions?: BaseOption[];
};

const LEFT_RIGHT_RATIO: ListViewProps['leftRightRatio'] = {
  left: { max: '240px', min: '240px' },
  right: { max: 'none' },
};

const SELECT_PROPS = { style: { width: '100%' } };

type FormValues = {
  viewers: string[];
};

/**
 * The host page keeps the selection in its own state and only learns about it
 * through this event, so the initial selection has to be announced too —
 * otherwise submitting without touching the select would send nothing.
 */
const dispatchViewerChange = (values: (string | number)[]) => {
  const ev = new CustomEvent('wc.ap.viewer.onchange', {
    detail: values,
    bubbles: true,
  });
  window.dispatchEvent(ev);
};

const InnerViewerSelect: FC<Props> = memo(
  ({ selectedItems, defaultOptions }) => {
    const { t } = useTranslation();
    /**
     * With nothing selected yet, the options handed in from the host page are
     * the initial selection — that is how the payee's in-house contacts end up
     * preselected on a report that has no viewer.
     */
    const initialViewers = useMemo(
      () =>
        selectedItems?.length
          ? selectedItems
          : (defaultOptions ?? []).map((option) => String(option.value)),
      [selectedItems, defaultOptions]
    );
    const { control } = useForm<FormValues>({
      defaultValues: { viewers: initialViewers },
    });
    const onChangeAfter = useCallback(
      (value: string | number | (string | number)[]) => {
        const values = Array.isArray(value) ? value : [value];
        dispatchViewerChange(values);
      },
      []
    );

    useEffect(() => {
      if (!initialViewers.length) return;
      dispatchViewerChange(initialViewers);
    }, [initialViewers]);

    return (
      <ListView leftRightRatio={LEFT_RIGHT_RATIO}>
        <Single>
          <ListViewLabel key='left'>{t('viewer_select_label')}</ListViewLabel>
          <OfficeMember<FormValues>
            control={control}
            name='viewers'
            inputId='viewers'
            defaultOptions={defaultOptions}
            selectProps={SELECT_PROPS}
            onChangeAfter={onChangeAfter}
          />
        </Single>
      </ListView>
    );
  }
);
InnerViewerSelect.displayName = 'InnerViewerSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ViewerSelect = memo((props: Props) => (
  <InnerViewerSelect {...props} />
)) as FC<Props>;
ViewerSelect.displayName = 'ViewerSelect';
